/* SPEC-457 — building the "Request a change?" pre-fill.
 *
 * Extracted from `ForecastThumbs` so the escalation seam is UNIT-TESTABLE: an acceptance
 * criterion says "escalation pre-fill works", and that should be verified rather than
 * asserted. Pure — no React, no router, no window.
 *
 * It describes what the reader did and nothing more. It must never promise an outcome: the
 * suggestion form it opens is the existing intake path, and whether anything happens is a
 * triage decision, not something this text can commit to.
 */

export interface ChangeRequestPrefill {
  title: string;
  description: string;
}

export interface ChangeRequestInput {
  /** Human label for the figure, e.g. "12620 week 2026-W42". */
  what: string;
  skuCode?: string;
  /** The forecast cycle THIS figure belongs to, where one is known. */
  period?: string;
  /** The optional line the reader typed with their thumbs-down. */
  comment?: string;
}

/** Query-param keys the sidebar's suggestion form consumes (then strips). ONE definition,
 *  so the producer and the consumer cannot drift apart. */
export const PREFILL_TITLE_PARAM = 'suggest_title';
export const PREFILL_DESC_PARAM = 'suggest_desc';

export function buildChangeRequest(input: ChangeRequestInput): ChangeRequestPrefill {
  const what = input.what.trim() || 'this forecast';
  const lines = [
    `I marked the forecast for ${what} as not useful.`,
    input.comment && input.comment.trim()
      ? `What I said was wrong: ${input.comment.trim()}`
      : '',
    input.skuCode ? `SKU: ${input.skuCode}` : '',
    input.period ? `Forecast cycle: ${input.period}` : '',
  ].filter(Boolean);
  return {
    title: `Forecast change request: ${what}`,
    description: lines.join('\n'),
  };
}

/** The search string that opens the suggestion form pre-filled, preserving the params the
 *  page is already carrying (a `?tab=` must survive — dropping it would move the reader to
 *  a different view as a side effect of asking for a change). */
export function buildPrefillSearch(
  existingSearch: string, prefill: ChangeRequestPrefill,
): string {
  const params = new URLSearchParams(existingSearch);
  params.set(PREFILL_TITLE_PARAM, prefill.title);
  params.set(PREFILL_DESC_PARAM, prefill.description);
  return params.toString();
}
